import { useState, useEffect, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, QrCode, Copy, Check, ExternalLink, RefreshCw, AlertCircle, TrendingUp, Download, Monitor, Smartphone, FileText } from "lucide-react";
import api, { formatDetail, fmtINR } from "@/lib/api";
import { VendorResponse } from "@/components/payment/VendorResponse";

const VIEW_MODE_KEY = "payin.viewMode"; // iframe | qr | popup
const METHOD_KEY = "payin.method";
const FLOW_KEY = "payin.flow.v3";
const VIEW_MODES = [
    { id: "iframe", label: "Inline cashier (iframe)", icon: Monitor, hint: "Opens the Payspher cashier panel on this page. Falls back to QR if the vendor blocks embedding." },
    { id: "qr", label: "QR of payment link", icon: Smartphone, hint: "Shows a scannable QR for the hosted link — ideal for walk-in customers." },
    { id: "popup", label: "Open in new tab", icon: ExternalLink, hint: "Launches the cashier page in a new browser tab the moment the order is created." },
];

export default function PanelCreatePayin() {
    const [amount, setAmount] = useState("");
    const [reqId, setReqId] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [order, setOrder] = useState(null);
    const [copied, setCopied] = useState("");
    const [polling, setPolling] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [todayStats, setTodayStats] = useState({ byMid: [], totalCaptured: 0, totalCount: 0 });
    const [mids, setMids] = useState([]);
    const [selectedMid, setSelectedMid] = useState("");
    const [submerchant, setSubmerchant] = useState("");
    const [submerchantOptions, setSubmerchantOptions] = useState([]);
    const [method, setMethod] = useState(() => localStorage.getItem(METHOD_KEY) || "HPP");
    const [flow, setFlow] = useState(() => localStorage.getItem(FLOW_KEY) || "");
    const [partnerFlow, setPartnerFlow] = useState(null);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_MODE_KEY) || "iframe");

    useEffect(() => { localStorage.setItem(VIEW_MODE_KEY, viewMode); }, [viewMode]);
    useEffect(() => { localStorage.setItem(METHOD_KEY, method); }, [method]);
    useEffect(() => { localStorage.setItem(FLOW_KEY, flow); }, [flow]);

    const loadTodayStats = async () => {
        try {
            const [ordersRes, midsRes, submRes] = await Promise.all([
                api.get("/partner/payin/orders?limit=200"),
                api.get("/partner/payin/mids"),
                api.get("/partner/payin/submerchants").catch(() => ({ data: { items: [] } })),
            ]);
            setSubmerchantOptions(submRes.data.items || []);
            const midLabel = {};
            for (const m of midsRes.data.items || []) midLabel[m.mid_id] = m.label;
            setMids(midsRes.data.items || []);
            setPartnerFlow(midsRes.data.partner_flow || null);
            if ((midsRes.data.items || []).length && !selectedMid) {
                setSelectedMid(midsRes.data.items[0].mid_id);
            }
            const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
            const todays = (ordersRes.data.items || []).filter(
                (o) => new Date(o.created_at) >= todayStart && o.status === "CAPTURED"
            );
            const byMidMap = {};
            for (const o of todays) {
                const k = o.mid_id;
                byMidMap[k] = byMidMap[k] || { mid_id: k, label: midLabel[k] || "Unknown MID", amount: 0, count: 0 };
                byMidMap[k].amount += o.amount || 0;
                byMidMap[k].count += 1;
            }
            setTodayStats({
                byMid: Object.values(byMidMap).sort((a, b) => b.amount - a.amount),
                totalCaptured: todays.reduce((s, o) => s + (o.amount || 0), 0),
                totalCount: todays.length,
            });
        } catch { /* non-fatal */ }
    };
    useEffect(() => { loadTodayStats(); }, []);

    const midFlow = (mids.find((m) => m.mid_id === selectedMid) || {}).default_flow || "direct";
    const defaultFlow = partnerFlow || midFlow;
    const effectiveFlow = flow || defaultFlow;

    const generate = async () => {
        setBusy(true); setErr(""); setOrder(null);
        try {
            const amt = parseFloat(amount);
            if (!amt || amt <= 0) { setErr("Enter a valid amount"); setBusy(false); return; }
            const ref = reqId.trim() || `qr_${Date.now()}`;
            const body = {
                amount: amt,
                client_reference_id: ref,
                method: effectiveFlow === "redirect" ? undefined : method,
                flow: flow || undefined,
                mid: selectedMid || undefined,
                submerchant_label: submerchant.trim() || undefined,
            };
            const r = await api.post("/partner/payin/orders", body);
            const o = r.data.order;
            setOrder(o);
            if (viewMode === "popup" && o?.payment_link && o.status !== "CAPTURED") {
                // Fire-and-forget popup
                try { window.open(o.payment_link, "_blank", "noopener"); } catch {}
            }
            if (o?.status === "CAPTURED") loadTodayStats();
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const verify = async () => {
        if (!order?.order_id) return;
        setPolling(true);
        try {
            const r = await api.post(`/partner/payin/orders/${order.order_id}/verify`);
            setOrder(r.data || r.data.order || order);
            if ((r.data || {}).status === "CAPTURED") loadTodayStats();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setPolling(false); }
    };

    const reset = () => { setOrder(null); setAmount(""); setReqId(""); setErr(""); setSubmerchant(""); };
    const copy = async (text, k) => {
        try { await navigator.clipboard.writeText(text); setCopied(k); setTimeout(() => setCopied(""), 1500); } catch {}
    };

    const exportCsv = async () => {
        setExporting(true);
        try {
            const r = await api.get("/partner/payin/orders/export-today", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            const today = new Date().toISOString().slice(0, 10);
            a.href = url; a.download = `payin-captures-${today}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message || "Export failed");
        } finally { setExporting(false); }
    };

    return (
        <div data-testid="panel-create-payin" className="p-6 max-w-5xl mx-auto">
            <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                    <QrCode className="w-6 h-6" />
                </div>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold">Create PayIn Transaction</h1>
                    <p className="text-sm text-slate-500 mt-0.5">Generate a payment QR via your assigned provider</p>
                </div>
                <button data-testid="cp-export-today" onClick={exportCsv} disabled={exporting || todayStats.totalCount === 0}
                        title={todayStats.totalCount === 0 ? "No captures today to export" : "Download today's captures as CSV"}
                        className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50">
                    {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    Export today's CSV
                </button>
            </div>

            {err && (
                <div data-testid="cp-err" className="mb-4 bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-700 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {err}
                </div>
            )}

            {/* Today's captured — by MID */}
            <section className="mb-5" data-testid="cp-today-stats">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                        <TrendingUp className="w-3.5 h-3.5" /> Today's captures
                    </h3>
                    <button onClick={loadTodayStats} className="text-[10px] text-slate-500 hover:text-indigo-600 flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <div className="text-lg font-bold font-mono text-emerald-700">{fmtINR(todayStats.totalCaptured)}</div>
                            <div className="text-[11px] text-slate-500">{todayStats.totalCount} captured today</div>
                        </div>
                        {todayStats.byMid.length === 0 && (
                            <div className="text-[11px] text-slate-400 italic">No captures yet today</div>
                        )}
                    </div>
                    {todayStats.byMid.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                            {todayStats.byMid.map((m) => (
                                <div key={m.mid_id} data-testid={`cp-mid-stat-${m.mid_id}`}
                                        className="border border-slate-100 rounded-md p-2 bg-slate-50/50">
                                    <div className="flex items-center justify-between">
                                        <div className="text-xs font-semibold text-slate-700 truncate" title={m.label}>{m.label}</div>
                                        <span className="px-1.5 py-0.5 rounded-full text-[9px] bg-indigo-100 text-indigo-800 font-bold">{m.count}</span>
                                    </div>
                                    <div className="text-sm font-bold font-mono text-slate-900 mt-0.5">{fmtINR(m.amount)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {!order ? (
                <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-5 shadow-sm">
                    <h2 className="text-base font-semibold">Transaction Details</h2>

                    {mids.length > 1 && (
                        <div data-testid="cp-mid-picker-wrap">
                            <label className="text-sm font-semibold text-slate-800">Pay to MID</label>
                            <select data-testid="cp-mid-picker" value={selectedMid}
                                        onChange={(e) => setSelectedMid(e.target.value)}
                                        className="input w-full mt-2 py-2.5 text-sm">
                                {mids.map((m) => (
                                    <option key={m.mid_id} value={m.mid_id}>{m.label}</option>
                                ))}
                            </select>
                            <p className="text-xs text-slate-500 mt-1.5">Pick which assigned MID to route this transaction through</p>
                        </div>
                    )}
                    {mids.length === 1 && (
                        <div data-testid="cp-mid-single" className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-600 flex items-center justify-between">
                            <span>Routing via <strong className="text-slate-900">{mids[0].label}</strong></span>
                            <span className="font-mono text-slate-400 text-[10px]">{mids[0].mid_id}</span>
                        </div>
                    )}
                    {mids.length === 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                            No payin MID assigned to your account. Ask your ops contact to assign one.
                        </div>
                    )}

                    <div>
                        <label className="text-sm font-semibold text-slate-800">Amount (₹)</label>
                        <div className="relative mt-2">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">₹</span>
                            <input data-testid="cp-amount" value={amount}
                                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                                    inputMode="decimal" placeholder="100.00"
                                    className="input w-full text-base font-medium pl-8 py-2.5 border-2 focus:border-indigo-500" />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-semibold text-slate-800">Request ID <span className="text-slate-400 font-normal">(optional)</span></label>
                        <input data-testid="cp-refid" value={reqId}
                                onChange={(e) => setReqId(e.target.value)}
                                placeholder="Auto-generated if left blank"
                                className="input w-full mt-2 py-2.5 font-mono text-sm" />
                        <p className="text-xs text-slate-500 mt-1.5">Use your own order ID for reconciliation</p>
                    </div>

                    <div>
                        <label className="text-sm font-semibold text-slate-800">Submerchant <span className="text-slate-400 font-normal">(optional)</span></label>
                        <input data-testid="cp-submerchant" value={submerchant} list="cp-submerchant-list" maxLength={40}
                                onChange={(e) => setSubmerchant(e.target.value)}
                                placeholder={submerchantOptions.length ? "Pick or type a brand label" : "e.g. brand-a, store-mumbai"}
                                className="input w-full mt-2 py-2.5 text-sm" />
                        <datalist id="cp-submerchant-list">
                            {submerchantOptions.map((s) => <option key={s} value={s} />)}
                        </datalist>
                        <p className="text-xs text-slate-500 mt-1.5">
                            Tag this capture to a sub-brand or store. Repeat values auto-complete from past orders.
                        </p>
                    </div>

                    {/* Method + Flow — advanced, defaults to Payspher-recommended */}
                    <details className="group">
                        <summary className="cursor-pointer text-xs font-semibold text-slate-500 hover:text-indigo-700 inline-flex items-center gap-1">
                            <span>Advanced routing</span>
                            <span className="text-[10px] text-slate-400 group-open:hidden">· {effectiveFlow === "redirect" ? "flow = redirect" : `method = ${method}, flow = ${effectiveFlow}`}</span>
                        </summary>
                        <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Payment method</label>
                                <select data-testid="cp-method" value={method} disabled={effectiveFlow === "redirect"} onChange={(e) => setMethod(e.target.value)}
                                            className="input text-sm mt-1 w-full py-2">
                                    <option value="HPP">HPP — Hosted cashier (recommended)</option>
                                    <option value="QR">QR — P2P QR image</option>
                                    <option value="UPI">UPI — UPI intent</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Request flow</label>
                                <select data-testid="cp-flow" value={flow} onChange={(e) => setFlow(e.target.value)}
                                            className="input text-sm mt-1 w-full py-2">
                                    <option value="">default — {defaultFlow} ({partnerFlow ? "partner setting" : "MID setting"})</option>
                                    <option value="direct">direct — HPP / QR / UPI</option>
                                    <option value="redirect">redirect — Payspher cashier picks method</option>
                                </select>
                            </div>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-2">
                            {effectiveFlow === "redirect"
                                ? <>Using <code className="bg-slate-100 px-1 rounded">redirect</code> — method is ignored; the customer chooses how to pay on the Payspher page.</>
                                : <>Using <code className="bg-slate-100 px-1 rounded">direct</code> with method <code className="bg-slate-100 px-1 rounded">{method}</code>.</>}
                        </p>
                    </details>

                    {/* View mode selector */}
                    <div data-testid="cp-view-mode">
                        <label className="text-sm font-semibold text-slate-800">How should the cashier open?</label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                            {VIEW_MODES.map((m) => {
                                const Icon = m.icon;
                                const active = viewMode === m.id;
                                return (
                                    <button key={m.id} type="button" data-testid={`cp-view-mode-${m.id}`}
                                            onClick={() => setViewMode(m.id)}
                                            className={`text-left p-3 rounded-lg border-2 transition-all ${active ? "border-indigo-500 bg-indigo-50/60 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                                        <div className="flex items-center gap-2">
                                            <Icon className={`w-4 h-4 ${active ? "text-indigo-600" : "text-slate-500"}`} />
                                            <span className={`text-xs font-bold ${active ? "text-indigo-900" : "text-slate-800"}`}>{m.label}</span>
                                        </div>
                                        <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">{m.hint}</p>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-2">Your choice is remembered on this browser for next time.</p>
                    </div>

                    <button data-testid="cp-generate" onClick={generate} disabled={busy || !amount || mids.length === 0}
                            className="w-full btn-primary py-3 rounded-md text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <QrCode className="w-5 h-5" />}
                        Create Transaction
                    </button>
                </section>
            ) : (
                <ResultCard order={order} onVerify={verify} polling={polling} onReset={reset}
                             copied={copied} onCopy={copy} viewMode={viewMode} />
            )}
        </div>
    );
}

function ResultCard({ order, onVerify, polling, onReset, copied, onCopy, viewMode }) {
    const captured = order.status === "CAPTURED";
    const failed = order.status === "FAILED";
    const qrSrc = order.qr_image_url || (order.qr_image_base64 ? `data:image/png;base64,${order.qr_image_base64}` : null);
    const link = order.payment_link;
    const [iframeBlocked, setIframeBlocked] = useState(false);
    const iframeRef = useRef(null);
    const loadTimer = useRef(null);

    // Detect iframe block: Payspher likely sends X-Frame-Options DENY.
    // We can't inspect cross-origin iframe content, so we race onLoad vs a timeout.
    useEffect(() => {
        if (viewMode !== "iframe" || !link || captured || failed) return;
        setIframeBlocked(false);
        loadTimer.current = setTimeout(() => setIframeBlocked(true), 4000);
        return () => clearTimeout(loadTimer.current);
    }, [viewMode, link, captured, failed]);

    const onIframeLoad = () => {
        clearTimeout(loadTimer.current);
        // Try to access iframe doc. If blocked, access throws / returns null.
        try {
            const doc = iframeRef.current?.contentDocument;
            if (doc && doc.location && doc.location.href === "about:blank") setIframeBlocked(true);
        } catch { /* cross-origin → ok, likely rendered */ }
    };

    // Decide what visual to show in the "payment surface" area
    const showIframe = viewMode === "iframe" && link && !iframeBlocked;
    const showClientQr = (viewMode === "qr" || iframeBlocked) && link;
    const showVendorQr = qrSrc && !showIframe && !showClientQr;

    return (
        <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5" data-testid="cp-result">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${captured ? "bg-emerald-100 text-emerald-800" : failed ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${captured ? "bg-emerald-600" : failed ? "bg-rose-600" : "bg-amber-500 animate-pulse"}`} />
                        {order.status}
                    </div>
                    <div className="mt-2 text-sm text-slate-600">
                        <strong className="text-slate-900 text-lg">₹{order.amount}</strong> · ref <code className="font-mono text-xs">{order.client_reference_id}</code>
                    </div>
                </div>
                <button onClick={onReset} data-testid="cp-new-txn" className="text-xs text-indigo-600 hover:underline">New transaction →</button>
            </div>

            {/* Payment surface: iframe / client QR / vendor QR */}
            {!captured && !failed && showIframe && (
                <div data-testid="cp-iframe-wrap" className="border border-slate-200 rounded-md overflow-hidden bg-white">
                    <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b border-slate-200">
                        <span className="text-[11px] text-slate-500 font-medium">Payspher cashier · live</span>
                        <a href={link} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-600 hover:underline inline-flex items-center gap-1">
                            Open full page <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                    <iframe ref={iframeRef} src={link} title="Payspher cashier"
                            data-testid="cp-iframe"
                            onLoad={onIframeLoad}
                            className="w-full h-[560px] bg-white" />
                </div>
            )}

            {!captured && !failed && showClientQr && (
                <div className="flex flex-col items-center bg-slate-50 border border-slate-200 rounded-md p-5" data-testid="cp-client-qr-wrap">
                    <QRCodeCanvas value={link} size={224} level="M" includeMargin
                                  className="bg-white p-2 rounded shadow" data-testid="cp-client-qr" />
                    <p className="text-xs text-slate-600 mt-3">
                        {iframeBlocked ? "Vendor blocked embedding — scan this QR instead" : "Ask the customer to scan this QR with any UPI app"}
                    </p>
                    <a href={link} target="_blank" rel="noreferrer" data-testid="cp-open-cashier"
                       className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800">
                        Open cashier in new tab <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                </div>
            )}

            {!captured && !failed && showVendorQr && (
                <div className="flex flex-col items-center bg-slate-50 border border-slate-200 rounded-md p-5">
                    <img src={qrSrc} alt="Payment QR" className="w-56 h-56 bg-white p-2 rounded shadow" data-testid="cp-qr-img" />
                    <p className="text-xs text-slate-600 mt-3">Ask the customer to scan this QR with any UPI app</p>
                </div>
            )}

            {/* Payment link row — always shown so partner can copy/share */}
            {link && (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                    {order.flow === "redirect" && !captured && !failed && (
                        <a href={link} target="_blank" rel="noreferrer" data-testid="cp-open-payment-page"
                           className="mb-3 w-full btn-primary py-2.5 rounded-md text-sm font-bold flex items-center justify-center gap-2">
                            Open payment page <ExternalLink className="w-4 h-4" />
                        </a>
                    )}
                    <div className="text-xs font-semibold text-slate-600 mb-2">Payment link</div>
                    <div className="flex items-center gap-2">
                        <a href={link} target="_blank" rel="noreferrer" data-testid="cp-link"
                            className="text-xs text-indigo-600 hover:underline font-mono break-all flex-1">{link}</a>
                        <button onClick={() => onCopy(link, "link")} className="p-1.5 text-slate-500 hover:text-indigo-600 shrink-0">
                            {copied === "link" ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                        </button>
                        <a href={link} target="_blank" rel="noreferrer" className="p-1.5 text-slate-500 hover:text-indigo-600 shrink-0">
                            <ExternalLink className="w-4 h-4" />
                        </a>
                    </div>
                    <ShareLink orderId={order.order_id} copied={copied} onCopy={onCopy} />
                </div>
            )}

            {order.response_message && (
                <div className={`text-xs rounded-md p-3 ${failed ? "bg-rose-50 text-rose-800 border border-rose-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                    {order.response_message}
                </div>
            )}
            <VendorResponse order={order} testid="cp-vendor-response" />

            {!captured && !failed && (
                <button onClick={onVerify} disabled={polling} data-testid="cp-verify"
                        className="w-full border border-slate-200 py-2.5 rounded-md text-sm font-medium flex items-center justify-center gap-2 hover:bg-slate-50">
                    {polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Check status
                </button>
            )}
            {captured && (
                <button data-testid="cp-receipt" onClick={async () => {
                        try {
                            const r = await api.get(`/partner/payin/orders/${order.order_id}/receipt.pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                            const a = document.createElement("a");
                            a.href = url; a.download = `receipt-${order.client_reference_id || order.order_id}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            URL.revokeObjectURL(url);
                        } catch {}
                    }}
                        className="w-full btn-primary py-2.5 rounded-md text-sm font-semibold flex items-center justify-center gap-2">
                    <FileText className="w-4 h-4" /> Download PDF receipt
                </button>
            )}
        </section>
    );
}

function ShareLink({ orderId, copied, onCopy }) {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/pay/go/${orderId}`;
    return (
        <div className="mt-3 pt-3 border-t border-slate-200" data-testid="cp-share-link-wrap">
            <div className="text-xs font-semibold text-slate-600 mb-1">Share with customer (your domain — redirects to the payment page)</div>
            <div className="flex items-center gap-2">
                <code data-testid="cp-share-link" className="text-xs font-mono text-slate-700 break-all flex-1">{url}</code>
                <button onClick={() => onCopy(url, "share")} data-testid="cp-share-copy" className="p-1.5 text-slate-500 hover:text-indigo-600 shrink-0">
                    {copied === "share" ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
}
